import type { GeneralSettings } from '../../types';
import { TIMEZONE_OPTIONS } from '../../utils/timezones';

interface GeneralSettingsTabProps {
  settings: GeneralSettings;
  onChange: (nextSettings: GeneralSettings) => void;
}

const INPUT_CLASS_NAME = 'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

export function GeneralSettingsTab({ settings, onChange }: GeneralSettingsTabProps) {
  const updateSetting = <K extends keyof GeneralSettings>(key: K, value: GeneralSettings[K]) => {
    onChange({ ...settings, [key]: value });
  };

  const autoCloseFrequency = settings.autoCloseFrequency ?? settings.autoCloseFrequencyMinutes ?? 60;

  return (
    <div className="bg-white p-6 shadow-sm dark:bg-gray-800">
      <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-gray-100">General Settings</h2>

      <div className="space-y-4">
        <div>
          <label htmlFor="support-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Support Email <span className="text-red-500">*</span>
          </label>
          <input
            id="support-email"
            type="email"
            className={INPUT_CLASS_NAME}
            value={settings.supportEmail}
            onChange={(event) => updateSetting('supportEmail', event.target.value)}
            placeholder="support@yourdomain.com"
            required
            aria-required="true"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Default support email for the system (used in email footers, etc.)
          </p>
        </div>

        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Default Timezone <span className="text-red-500">*</span>
          </label>
          <select
            id="timezone"
            className={INPUT_CLASS_NAME}
            value={settings.defaultTimezone}
            onChange={(event) => updateSetting('defaultTimezone', event.target.value)}
            required
            aria-required="true"
          >
            {TIMEZONE_OPTIONS.map((timezone) => (
              <option key={timezone.value} value={timezone.value}>
                (UTC{timezone.offset}) {timezone.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Default timezone for the system (can be overridden at tenant level)
          </p>
        </div>

        <div>
          <label htmlFor="invitationExpiration" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Invitation Expiration (Days) <span className="text-red-500">*</span>
          </label>
          <input
            id="invitationExpiration"
            type="number"
            className={INPUT_CLASS_NAME}
            min={1}
            max={90}
            value={settings.invitationExpirationDays ?? 7}
            onChange={(event) => updateSetting('invitationExpirationDays', parseInt(event.target.value, 10) || 7)}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Number of days before user invitations expire (default: 7 days, can be overridden at tenant level)
          </p>
        </div>

        <div>
          <label htmlFor="ci-retirement-grace-period-days" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            CI Auto-Retirement Grace Period (Days)
          </label>
          <input
            id="ci-retirement-grace-period-days"
            type="number"
            className={INPUT_CLASS_NAME}
            min={0}
            max={365}
            value={settings.ciRetirementGracePeriodDays ?? 14}
            onChange={(event) => updateSetting('ciRetirementGracePeriodDays', parseInt(event.target.value, 10) || 0)}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Default number of days a discovered CI can remain missing before auto-retirement. Tenants can override this value; 0
            disables auto-retirement.
          </p>
        </div>

        <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
          <div className="mb-3 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Auto-Close Configuration</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Configure the default auto-close behavior for resolved incidents when a tenant has no override.
              </p>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={Boolean(settings.autoCloseEnabled)}
                onChange={(event) => updateSetting('autoCloseEnabled', event.target.checked)}
                aria-label="Enable or disable default auto-close"
              />
              <div className="h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-primary-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-gray-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                {settings.autoCloseEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>

          {settings.autoCloseEnabled && (
            <div>
              <label htmlFor="autoCloseDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Default Auto-Close Days
              </label>
              <input
                id="autoCloseDays"
                type="number"
                className={INPUT_CLASS_NAME}
                min={1}
                max={365}
                value={settings.autoCloseDays ?? 7}
                onChange={(event) => updateSetting('autoCloseDays', parseInt(event.target.value, 10) || 7)}
              />

              <div className="mt-3">
                <label htmlFor="autoclose-frequency" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Check Frequency
                </label>
                <select
                  id="autoclose-frequency"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  value={autoCloseFrequency}
                  onChange={(event) => updateSetting('autoCloseFrequency', parseInt(event.target.value, 10))}
                  disabled={!settings.autoCloseEnabled}
                >
                  <option value={5}>Every 5 minutes</option>
                  <option value={10}>Every 10 minutes</option>
                  <option value={15}>Every 15 minutes</option>
                  <option value={30}>Every 30 minutes</option>
                  <option value={60}>Every hour</option>
                  <option value={120}>Every 2 hours</option>
                  <option value={240}>Every 4 hours</option>
                  <option value={480}>Every 8 hours</option>
                  <option value={720}>Every 12 hours</option>
                  <option value={1440}>Every 24 hours</option>
                </select>
              </div>

              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Number of days after resolution before incidents are auto-closed by default.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
